import React from 'react';
import ListingPreview from 'assets/marketing/klarity-listing-preview.png';
import GrowthJourneyStepper from './GrowthJourneyStepper';
import s from './s.module.less';

type Props = {
    isGroupPractice?: boolean;
    stdListingCount?: number;
    proListingCount?: number;
    onSetupClick?: () => void;
};

const GiftIcon = () => (
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ flexShrink: 0 }}>
        <rect x="5" y="14" width="30" height="20" rx="2" fill="#F59E0B" />
        <rect x="5" y="14" width="30" height="6" fill="#F97316" />
        <rect x="17" y="14" width="6" height="20" fill="#FBBF24" />
        <rect x="3" y="11" width="34" height="6" rx="1" fill="#F97316" />
        <rect x="17" y="11" width="6" height="6" fill="#FCD34D" />
        <path d="M14 11 C14 6, 18 6, 20 11 C22 6, 26 6, 26 11" stroke="#F97316" strokeWidth="2" fill="none" />
    </svg>
);

const CheckIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="3 9 7 13 15 5" />
    </svg>
);

const BoltIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#F59E0B" stroke="none">
        <path d="M8 1 L4 9 L7 9 L6 15 L12 6 L9 6 L10 1 Z" />
    </svg>
);

const BENEFITS = [
    {
        icon: (
            <span className={s.benefitIconCircle} style={{ background: '#FEF3C7' }}>
                👁️
            </span>
        ),
        title: 'Get seen first',
        desc: 'Your profile can appear at the very top when patients search. This puts you ahead of others and makes it easier for patients to choose you.',
    },
    {
        icon: (
            <span className={s.benefitIconSquare} style={{ background: '#10B981' }}>
                <CheckIcon />
            </span>
        ),
        title: 'Reach more of the right patients',
        desc: 'Whether you want more weight loss, skin care, primary care, or mental health patients, Pro-Listings help you show up for the types of patients you want to see.',
    },
    {
        icon: (
            <span className={s.benefitIconCircle} style={{ background: '#FEF3C7' }}>
                💰
            </span>
        ),
        title: 'Pay only when you get a booking',
        desc: 'You are only charged when a new patient books an appointment. There are no fees for views, clicks, or impressions. The Pro-Listing fee is separate from the standard $120 lead fee.',
    },
    {
        icon: (
            <span className={s.benefitIconCircle} style={{ background: '#F3F4F6' }}>
                ⚙️
            </span>
        ),
        title: "You're always in control",
        desc: 'You can turn your ads on or off at any time. Adjust them based on your schedule, goals, or patient load.',
    },
    {
        icon: (
            <span className={s.benefitIconCircle} style={{ background: '#DBEAFE' }}>
                <BoltIcon />
            </span>
        ),
        title: 'Twice the exposure',
        desc: "With a competitive bid, your profile can appear twice on a patient's screen — once in the featured spot and again in the regular results. This doubles the chance of being chosen.",
    },
];

const CampaignIntro: React.FC<Props> = ({
    isGroupPractice,
    stdListingCount = 0,
    proListingCount = 0,
    onSetupClick,
}) => {
    return (
        <div>
            {isGroupPractice && (
                <GrowthJourneyStepper stdListingCount={stdListingCount} proListingCount={proListingCount} />
            )}
            <div className={s.introCard}>
                <div className={s.heroRow}>
                    <div className={s.heroLeft}>
                        <div>
                            <div className={s.heroTitle}>
                                Grow your practice faster with <span className={s.heroHighlight}>Pro-Listing</span>
                            </div>
                            <div className={s.heroSubtitle}>
                                Show up at the top of search results and get discovered by more types of patients.
                            </div>
                        </div>
                        <div className={s.creditBanner}>
                            <GiftIcon />
                            <div className={s.creditBannerText}>
                                <div className={s.creditTitle}>
                                    Get <strong>$150 free credit</strong> for your first Pro-Listings campaign
                                </div>
                                <div className={s.creditSubtitle}>85% of providers choose Pro-Listings</div>
                            </div>
                        </div>
                        <div>
                            <button className={s.setupBtn} onClick={onSetupClick}>
                                Set up now
                            </button>
                            <div className={s.setupDisclaimer}>
                                No commitment. You can turn Pro-Listing off anytime.
                            </div>
                        </div>
                    </div>

                    <div className={s.heroRight}>
                        <svg
                            width="100%"
                            height="240"
                            viewBox="0 0 480 240"
                            preserveAspectRatio="none"
                            className={s.heroChart}
                        >
                            <defs>
                                <linearGradient id="proHeroFillSolo" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#FB923C" stopOpacity="0.28" />
                                    <stop offset="100%" stopColor="#FB923C" stopOpacity="0" />
                                </linearGradient>
                            </defs>
                            <path
                                d="M0,210 C70,200 130,195 190,180 C250,165 300,140 350,100 C390,68 425,40 460,18 L460,240 L0,240 Z"
                                fill="url(#proHeroFillSolo)"
                            />
                            <path
                                d="M0,210 C70,200 130,195 190,180 C250,165 300,140 350,100 C390,68 425,40 460,18"
                                fill="none"
                                stroke="#F97316"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                            <circle cx="460" cy="18" r="7" fill="#F97316" />
                        </svg>
                        <div className={s.heroFloatingCard}>
                            <div className={s.heroFloatingTitle}>Get more exposure</div>
                            <div className={s.heroFloatingDesc}>Reach more patients and grow your practice faster</div>
                        </div>
                    </div>
                </div>

                <div className={s.benefitsRow}>
                    <div className={s.previewCol}>
                        <img
                            src={ListingPreview}
                            alt="Klarity provider listing preview"
                            width="418"
                            className={s.previewImg}
                        />
                    </div>
                    <div className={s.benefitsList}>
                        {BENEFITS.map((b) => (
                            <div key={b.title} className={s.benefitItem}>
                                {b.icon}
                                <div>
                                    <div className={s.benefitTitle}>{b.title}</div>
                                    <div className={s.benefitDesc}>{b.desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={s.introFooter}>
                    <button className={s.setupBtnFull} onClick={onSetupClick}>
                        Set up now
                    </button>
                    <div className={s.introFooterDisclaimer}>No commitment. You can turn Pro-Listing off anytime.</div>
                </div>
            </div>
        </div>
    );
};

export default CampaignIntro;
